/**
 * @file Apollo helper utilities.
 */

import get from 'lodash.get';
import produce from 'immer';

/**
 * Creates a "load more" method.
 *
 * @param {Object} options - The options.
 * @param {Object} options.source - The source object as returned from GQL.
 * @param {String} options.recordsPath - The path inside the query object to look for the records as expected by lodash.get.
 * @param {String} [options.offsetVariableLabel] - The name of the "offset" variable to be used on the query.
 * @param {Function} options.fetchMore - Apollo's fetchMore function.
 * @param {number} options.limit The limit.
 * @returns {Function}
 */
const createLoadMoreMethod = ({
  source,
  recordsPath,
  offsetVariableLabel = 'offset',
  limit,
  fetchMore,
}) => {
  if (!fetchMore) {
    return undefined;
  }

  return () =>
    fetchMore({
      variables: {
        limit,
        [offsetVariableLabel]: get(
          source,
          [recordsPath, 'records'].filter(Boolean).join('.'),
          [],
        ).length,
      },
      updateQuery: (prevResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prevResult;
        }

        return produce(prevResult, draft => {
          const key = Object.keys(prevResult)[0];

          get(
            draft,
            [key, recordsPath, 'records'].filter(Boolean).join('.'),
            [],
          ).push(
            ...get(
              fetchMoreResult,
              [key, recordsPath, 'records'].filter(Boolean).join('.'),
              [],
            ),
          );
        });
      },
    });
};

export { createLoadMoreMethod };
